import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectTheme } from "../../../Store/features/design/designSlice";
import { 
  getFilteredInvestorsListById, 
  removeInvestorFromList,
  removeVcFromList
} from "../../../Service/user";
import { toast } from "react-toastify";
import InvestorListView from "./InvestorListView";
import VcListView from "./VcListView";
import "./ListDetail.scss";

const ListDetail = () => {
  const { listId } = useParams();
  const theme = useSelector(selectTheme);
  const navigate = useNavigate();
  
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedInvestors, setSelectedInvestors] = useState([]);
  const [selectedVcs, setSelectedVcs] = useState([]);
  const [removingItems, setRemovingItems] = useState(false);
  const [activeTab, setActiveTab] = useState('investors'); // 'investors' or 'vcs'
  
  useEffect(() => {
    const fetchListDetails = async () => {
      try {
        setLoading(true);
        const response = await getFilteredInvestorsListById(listId);
        if (response.status === 200) {
          setList(response.list);
        } else {
          toast.error("Failed to load list details");
          navigate("/campaign");
        }
      } catch (error) {
        console.error("Error fetching list details:", error);
        toast.error("An error occurred while loading list details");
        navigate("/campaign");
      } finally {
        setLoading(false);
      }
    };
    
    fetchListDetails();
  }, [listId, navigate]);
  
  const toggleInvestorSelection = (investorId) => {
    setSelectedInvestors(prev => 
      prev.includes(investorId)
        ? prev.filter(id => id !== investorId)
        : [...prev, investorId]
    );
  };

  const toggleVcSelection = (vcId) => {
    setSelectedVcs(prev => 
      prev.includes(vcId)
        ? prev.filter(id => id !== vcId)
        : [...prev, vcId]
    );
  };
  
  const handleRemoveItems = async () => {
    const selectedItems = activeTab === 'investors' ? selectedInvestors : selectedVcs;
    if (selectedItems.length === 0) {
      toast.info(`Please select ${activeTab === 'investors' ? 'investors' : 'VCs'} to remove`);
      return;
    }
    
    try {
      setRemovingItems(true);
      // Process each item removal sequentially
      for (const itemId of selectedItems) {
        if (activeTab === 'investors') {
          await removeInvestorFromList(listId, itemId);
        } else {
          await removeVcFromList(listId, itemId);
        }
      }
      
      toast.success(`Successfully removed ${selectedItems.length} ${activeTab === 'investors' ? 'investor(s)' : 'VC(s)'}`);
      
      // Refresh list data
      const response = await getFilteredInvestorsListById(listId);
      setList(response.list);
      if (activeTab === 'investors') {
        setSelectedInvestors([]);
      } else {
        setSelectedVcs([]);
      }
    } catch (error) {
      console.error("Error removing items:", error);
      toast.error(`An error occurred while removing ${activeTab === 'investors' ? 'investors' : 'VCs'}`);
    } finally {
      setRemovingItems(false);
    }
  };
  
  const renderSkeletonHeader = () => (
    <div className="listHeader">
      <div className="listInfo">
        <div className="skeleton" style={{ width: 200, height: 32, marginBottom: 10 }}></div>
        <div className="skeleton" style={{ width: 300, height: 20, marginBottom: 10 }}></div>
        <div className="skeleton" style={{ width: 250, height: 16 }}></div>
      </div>
    </div>
  );
  
  if (loading) {
    return (
      <div className={`listDetailContainer ${theme}`}>
        {renderSkeletonHeader()}
        <div className="investorsContainer">
          <div className="skeleton-grid">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="skeleton-card">
                <div className="skeleton" style={{ width: 60, height: 60, borderRadius: '50%', marginRight: 15 }}></div>
                <div className="skeleton-content">
                  <div className="skeleton" style={{ width: 200, height: 20, marginBottom: 8 }}></div>
                  <div className="skeleton" style={{ width: 150, height: 16, marginBottom: 6 }}></div>
                  <div className="skeleton" style={{ width: 120, height: 14 }}></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  
  if (!list) {
    return (
      <div className={`listDetailContainer ${theme}`}>
        <div className="errorState">List not found</div>
      </div>
    );
  }
  
  return (
    <div className={`listDetailContainer ${theme}`}>
      <div className="listHeader">
        <div className="listInfo">
          <h1>{list.name}</h1>
          <p className="listDescription">{list.description}</p>
          <p className="listMeta">
            <span>{list.investors?.length || 0} investors</span>
            <span>•</span>
            <span>{list.vcs?.length || 0} VCs</span>
            <span>•</span>
            <span>Created by {list.createdBy?.firstName} {list.createdBy?.lastName}</span>
          </p>
        </div>
        <div className="listActions">
          <button className="backButton" onClick={() => navigate("/campaign")}>
            Back to Lists
          </button>
          {(activeTab === 'investors' ? selectedInvestors.length : selectedVcs.length) > 0 && (
            <button 
              className="removeButton" 
              onClick={handleRemoveItems}
              disabled={removingItems}
            >
              {removingItems ? (
                <>
                  <span className="button-loader"></span>
                  Removing...
                </>
              ) : (
                `Remove Selected (${activeTab === 'investors' ? selectedInvestors.length : selectedVcs.length})`
              )}
            </button>
          )}
        </div>
      </div>

      <div className="tabsContainer">
        <button 
          className={`tabButton ${activeTab === 'investors' ? 'active' : ''}`}
          onClick={() => setActiveTab('investors')}
        >
          Investors ({list.investors?.length || 0})
        </button>
        <button 
          className={`tabButton ${activeTab === 'vcs' ? 'active' : ''}`}
          onClick={() => setActiveTab('vcs')}
        >
          VCs ({list.vcs?.length || 0})
        </button>
      </div>
      
      <div className="itemsContainer">
        {activeTab === 'investors' ? (
          <InvestorListView 
            investors={list.investors || []} 
            theme={theme}
            toggleInvestorSelection={toggleInvestorSelection}
            selectedInvestors={selectedInvestors}
          />
        ) : (
          <VcListView 
            vcs={list.vcs || []} 
            theme={theme}
            toggleVcSelection={toggleVcSelection}
            selectedVcs={selectedVcs}
          />
        )}
      </div>
    </div>
  );
};

export default ListDetail; 